/* istanbul ignore file */
import React, { useEffect, useMemo, useState, memo, useRef } from 'react'
import styled from 'styled-components'
import { useParams } from 'react-router-dom'
import { Alert, Box, Space } from '@ally/metronome-ui'
// import allytm from '@ally/allytm'
import { deposit as afgDeposit } from '@ally/data-selectors-afg-accounts'
import { useHostServices } from '../HostServices'
import {
  SpendingBucketsProvider,
  SpendingBucketsCategoriesProvider,
  PreferenceProvider,
  ProductsProvider,
} from '../services'
import { useAccountDetailsContext } from '../services/AccountDetailsProvider'
import { CoverDraftNotification } from './Checking/CoverDraftNotification'
import { PrintCheck } from './common/PrintCheck'
import { CheckDetails } from '../types'
import { amountFormatter, maskAccountNumber } from '../utils/formatter'
import { TransactionHistory } from './common/TransactionHistory'
import { AccountDetailsSkeletonLoader } from './common/AccountDetailSkeletonLoader'
import { CertificateOfDepositAccountDetails } from './CertificateOfDeposit/CertificateOfDepositAccountDetails'

// --- LAZY LOADED COMPONENTS --- //
const CheckingAccountDetails = React.lazy(
  () =>
    import(
      /* webpackChunkName: "CheckingDetails" */ './Checking/CheckingAccountDetails'
    ),
)
const CmgAccountDetails = React.lazy(
  () => import(/* webpackChunkName: "CmgDetails" */ './Cmg/CmgAccountDetails'),
)
const MoneyMarketAccountDetails = React.lazy(
  () =>
    import(
      /* webpackChunkName: "MoneyMarketDetails" */ './MoneyMarket/MoneyMarketAccountDetails'
    ),
)
const SavingsAccount = React.lazy(
  () =>
    import(/* webpackChunkName: "SavingsDetails" */ './Savings/SavingsAccount'),
)
// ------------------------------ //

/* TODO - remove the print logic from this file and put into a context.
  This component can be renamed to "AccountTypeLoader" since its new job
  is to load the account type and status as early as possible from several
  potential sources. This file can then call the appropriate component based
  on account type and wrap that component with a "print content" wrapper
  controlled by the print context */

const PrintingWrapper = styled.div<{ isPrintCheck: boolean }>`
  ${({ isPrintCheck }): string => `
  display: none;
  @media print {
    display: ${
      // TODO - add test coverage with an integration test
      /* istanbul ignore next */
      isPrintCheck ? 'block' : 'none'
    };
  }
  `}
`

const NonPrintingWrapper = styled.div<{ isPrintCheck: boolean }>`
  ${({ isPrintCheck }): string => `
  @media print {
    display: ${
      // TODO - add test coverage with an integration test
      /* istanbul ignore next */
      isPrintCheck ? 'none' : 'block'
    };
  }
  `}
`

export const AccountDetailsLoader: React.FC = memo(() => {
  const { hostData } = useHostServices()
  const { id } = useParams<{ id: string }>()
  const { accountDetails, hasAccountDetailsError, isAccountDetailsLoading } =
    useAccountDetailsContext()

  const [loadedAccountType, setLoadedAccountType] = useState<string | null>(
    null,
  )
  // work-around to keep track of previously loaded accountId
  const accountIdRef = useRef<string>(id)

  const [isPrintCheck, setPrintCheck] = useState(false)
  const [printCheckDetails, setPrintCheckDetails] = useState<CheckDetails>({})

  const [isAccountActive, setIsAccountActive] = useState<boolean | null>(null)

  const resetAccount = (): void => {
    setLoadedAccountType(null)
    setIsAccountActive(null)
  }

  useEffect(() => {
    accountIdRef.current = id
    resetAccount()
  }, [id])

  // looks for the account type and account status in the first available source and then stops looking
  useEffect(() => {
    if (!loadedAccountType) {
      /* istanbul ignore next */
      const afgAccountType = afgDeposit.nonRetirement
        .getAll(hostData)
        .find(account => account.id === id)?.type
      /* istanbul ignore next */
      const accountType = accountDetails?.accountType || afgAccountType || null
      /* istanbul ignore else */
      if (accountType && typeof accountType === 'string') {
        setLoadedAccountType(accountType.toLowerCase())
      }
      /* istanbul ignore next */
      const afgAccountStatus = afgDeposit.nonRetirement
        .getAll(hostData)
        .find(account => account.id === id)?.status
      /* istanbul ignore next */
      const accountStatus =
        accountDetails?.accountStatus || afgAccountStatus || null
      /* istanbul ignore else */
      if (accountStatus && typeof accountStatus === 'string') {
        setIsAccountActive(accountStatus.toLowerCase() === 'active')
      }
    }
  }, [hostData.afgAccounts.data, accountDetails, loadedAccountType, id])

  const hideTransactionsHistory = useMemo(() => {
    // if account type is an active dda or sda, do not render table here
    // for these account types we are loading the table somewhere else so we hide to avoid duplicates
    // for IRA accounts we have to display the table even though is SDA

    return (
      (loadedAccountType === 'dda' ||
        (loadedAccountType === 'sda' &&
          !accountDetails?.retirementAccountIndicator)) &&
      isAccountActive
    )
  }, [loadedAccountType])

  const transactionHistoryHeader = React.useRef<HTMLHeadingElement>(null)

  // TODO - add test coverage with an integration test
  /* istanbul ignore next */
  const afterPrint = (): void => {
    requestAnimationFrame(() => {
      /* istanbul ignore next */ // no need to testing focus ref branches
      transactionHistoryHeader.current?.focus()
    })
  }
  // TODO - Find solution for window mock
  /* istanbul ignore next */
  const printCheckHandler = (checkDetails: CheckDetails): void => {
    setPrintCheckDetails({
      ...checkDetails,
      accountNumber: maskAccountNumber(accountDetails?.accountNumberPvtEncrypt),
      accountNickname: accountDetails?.accountNickname,
      productDescription: accountDetails?.productDescription,
      availableBalance: accountDetails
        ? amountFormatter.format(accountDetails?.availableBalancePvtEncrypt)
        : '',
    })
    setPrintCheck(true)
    setTimeout(() => {
      // Wait a maximum of 2s
      window.print()
      setPrintCheck(false)
    }, 2000)

    window.onafterprint = afterPrint
  }

  const renderAccountType = (): React.ReactElement => {
    // checking the account type in hostData
    switch (loadedAccountType) {
      case 'cda':
        return (
          <ProductsProvider>
            <CertificateOfDepositAccountDetails />
          </ProductsProvider>
        )
      case 'dda':
        return (
          <SpendingBucketsProvider>
            <SpendingBucketsCategoriesProvider>
              <CheckingAccountDetails
                printCheckCallback={printCheckHandler}
                transactionHistoryHeaderRef={transactionHistoryHeader}
                isAccountActive={isAccountActive}
              />
            </SpendingBucketsCategoriesProvider>
          </SpendingBucketsProvider>
        )
      case 'mma':
        return <MoneyMarketAccountDetails />
      /* istanbul ignore next */ // TODO - add coverage
      case 'cmg':
        return <CmgAccountDetails />
      /* istanbul ignore next */ // TODO - add coverage
      case 'sda':
        return (
          <SavingsAccount
            printCheckCallback={printCheckHandler}
            transactionHistoryHeaderRef={transactionHistoryHeader}
          />
        )
      /* istanbul ignore next */
      default:
        return (
          <SpendingBucketsProvider>
            <SpendingBucketsCategoriesProvider>
              <CheckingAccountDetails
                printCheckCallback={printCheckHandler}
                transactionHistoryHeaderRef={transactionHistoryHeader}
                isAccountActive={isAccountActive}
              />
            </SpendingBucketsCategoriesProvider>
          </SpendingBucketsProvider>
        )
    }
  }

  return (
    <>
      <NonPrintingWrapper isPrintCheck={isPrintCheck}>
        {hasAccountDetailsError && (
          <Box data-testid="commonErrorMessage" padding="md">
            <Alert
              variant="error"
              disableContainer
              heading="There was a problem loading your account information. Try again later."
            />
          </Box>
        )}
        {isAccountDetailsLoading && <AccountDetailsSkeletonLoader />}
        {!!(
          !isAccountDetailsLoading &&
          accountDetails &&
          accountDetails.coverDraftRiskLevel &&
          accountDetails.takeActionDate
        ) && (
          <Space p="md">
            <Alert
              variant="warning"
              mb="28px"
              body={
                <CoverDraftNotification
                  riskLevel={accountDetails.coverDraftRiskLevel}
                  actionDate={accountDetails.takeActionDate}
                />
              }
            />
          </Space>
        )}
        <PreferenceProvider>
          {!isAccountDetailsLoading && loadedAccountType && (
            <React.Suspense fallback={<AccountDetailsSkeletonLoader />}>
              {renderAccountType()}
            </React.Suspense>
          )}
        </PreferenceProvider>
        {!hideTransactionsHistory && loadedAccountType && (
          <TransactionHistory
            printCheckCallback={printCheckHandler}
            headerRef={transactionHistoryHeader}
          />
        )}
      </NonPrintingWrapper>
      <PrintingWrapper isPrintCheck={isPrintCheck}>
        <PrintCheck printCheckDetails={printCheckDetails} />
      </PrintingWrapper>
    </>
  )
})
